import { PLATFORMS, type SgPlatform } from "./platforms";
import { originsOfImei, type SgOrigins } from "./origins";

// Lógica del Cuadre de SG, pura para testearla sin Supabase ni socket. Cada
// unidad de SG se cruza con: su cuenta (facturable, etiqueta, origen elegido a
// mano), las plataformas donde está dada de alta (sg-realtime) y su último
// reporte. De ahí salen las vistas rápidas, los filtros y la vista por cuenta.

export interface RawDevice {
  id: number;
  imei: string;
  name: string | null;
  plates: string | null;
  userId: number | null;
  active: boolean;
  condition: string | null;
  lastSg: number | null; // devices.last_time_connected, respaldo si el socket no lo trae
  // Sólo para informes y exportaciones; el cuadre en pantalla no los usa.
  phone?: string | null;
  simcard?: string | null;
  deviceType?: string | null; // marca + modelo
  createdAt?: number | null; // alta en SG (activation si existe, si no created_at)
  updatedAt?: number | null;
}

export interface RawAccount {
  id: number;
  name: string;
  billable: boolean;
  condition: string | null;
  origin: string[];
  company?: string | null; // razón social (billings), si no users.company
  v5CompanyId?: string | null; // origin_ids.v5: liga la cuenta con su compañía de v5
  billingInfo?: { taxId: string; email: string; phone: string; city: string; state: string } | null;
}

// Facturable = equipo activo de cuenta Facturable; mismo criterio que el
// status=billable de sg-realtime. Inactivo pesa más que la cuenta: un equipo
// dado de baja no se cobra aunque la cuenta sea facturable.
export type Billing = "billable" | "notBillable" | "inactive";

// 24 h es el corte de «reporta» de los tableros de entrega (REPORTING_DEFAULTS);
// 7 días separa la unidad que se cayó esta semana de la abandonada.
export type Report = "ok" | "week" | "old" | "none";
export const DAY_MS = 86_400_000;

export interface CuadreUnit {
  id: number;
  imei: string;
  name: string;
  plates: string;
  accountId: number | null;
  accountName: string;
  billing: Billing;
  condition: string | null;
  accountCondition: string | null;
  platforms: SgPlatform[];
  lastReport: number | null;
  report: Report;
  accountMismatch: boolean;
  phone: string;
  simcard: string;
  deviceType: string;
  createdAt: number | null;
  updatedAt: number | null;
  accountCompany: string;
  dupImei: boolean; // otro equipo activo de SG tiene el mismo IMEI
  tagMismatch: boolean; // activo con una etiqueta que en SG cuelga de Inactivo
}

export const reportOf = (ts: number | null, now: number): Report =>
  ts == null ? "none" : now - ts < DAY_MS ? "ok" : now - ts < 7 * DAY_MS ? "week" : "old";

// seed_conditions.sql y add_almacen_condition.sql cuelgan estas etiquetas del
// estado Inactivo: un equipo Activo con alguna de ellas se cobra y no debería.
// Medido en producción el 27-oct-2026: 1 equipo facturable con «Baja total».
export const INACTIVE_TAGS = ["Baja temporal", "Baja total", "Suspendido", "Almacen"];

const sameSet = (a: string[], b: string[]) => a.length === b.length && a.every((x) => b.includes(x));

export function buildUnits(
  devices: RawDevice[],
  accounts: RawAccount[],
  origins: SgOrigins,
  lastReports: Map<string, number>,
  now: number
): CuadreUnit[] {
  const byId = new Map(accounts.map((a) => [a.id, a]));
  const units = devices.map((d) => {
    const a = d.userId != null ? byId.get(d.userId) : undefined;
    const last = lastReports.get(d.imei) ?? d.lastSg;
    return {
      id: d.id,
      imei: d.imei,
      name: d.name ?? "",
      plates: d.plates ?? "",
      accountId: d.userId,
      accountName: a?.name ?? "",
      billing: (!d.active ? "inactive" : a?.billable ? "billable" : "notBillable") as Billing,
      condition: d.condition,
      accountCondition: a?.condition ?? null,
      platforms: PLATFORMS.filter((p) => originsOfImei(origins, d.imei).includes(p)),
      lastReport: last ?? null,
      report: reportOf(last ?? null, now),
      accountMismatch: false,
      phone: d.phone ?? "",
      simcard: d.simcard ?? "",
      deviceType: d.deviceType ?? "",
      createdAt: d.createdAt ?? null,
      updatedAt: d.updatedAt ?? null,
      accountCompany: a?.company || a?.name || "",
      dupImei: false,
      tagMismatch: d.active && !!d.condition && INACTIVE_TAGS.includes(d.condition),
    };
  });
  // IMEI repetido entre equipos activos: el mismo GPS cobrado dos veces. Medido
  // en producción el 27-oct-2026: 7 IMEI en dos equipos activos y facturables
  // (3 dentro de una misma cuenta, 4 entre cuentas distintas).
  const activeByImei = new Map<string, number>();
  for (const u of units) if (u.imei && u.billing !== "inactive") activeByImei.set(u.imei, (activeByImei.get(u.imei) ?? 0) + 1);
  for (const u of units) u.dupImei = u.billing !== "inactive" && (activeByImei.get(u.imei) ?? 0) > 1;
  // Cuenta descuadrada: el origen elegido en SG no coincide con la unión de las
  // plataformas de sus unidades. Sólo cuentas con alguna unidad en alguna.
  const real = new Map<number, Set<SgPlatform>>();
  for (const u of units) {
    if (u.accountId == null || !u.platforms.length) continue;
    const s = real.get(u.accountId) ?? new Set<SgPlatform>();
    u.platforms.forEach((p) => s.add(p));
    real.set(u.accountId, s);
  }
  const mismatched = new Set<number>();
  for (const [id, set] of real) {
    const sg = PLATFORMS.filter((p) => byId.get(id)?.origin.includes(p));
    if (!sameSet(sg, PLATFORMS.filter((p) => set.has(p)))) mismatched.add(id);
  }
  for (const u of units) u.accountMismatch = u.accountId != null && mismatched.has(u.accountId);
  return units;
}

// ── Filtros ─────────────────────────────────────────────────────────────────

export type PlatformFilter = SgPlatform | "none" | "multi";
// «silent» = cualquier cosa que no sea reporte en 24 h (1-7 d, más de 7 o sin dato).
export type ReportFilter = Report | "silent";
export type CheckFilter = "dupImei" | "tagMismatch";

export interface UnitFilters {
  q: string;
  billing: Billing | "";
  report: ReportFilter | "";
  platform: PlatformFilter | "";
  condition: string; // etiqueta de equipo; "" = cualquiera
  account: number | null;
  mismatch: boolean;
  check: CheckFilter | "";
}

export const EMPTY_FILTERS: UnitFilters = {
  q: "",
  billing: "",
  report: "",
  platform: "",
  condition: "",
  account: null,
  mismatch: false,
  check: "",
};

const norm = (s: string) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

const matchPlatform = (u: CuadreUnit, p: PlatformFilter) =>
  p === "none" ? u.platforms.length === 0 : p === "multi" ? u.platforms.length > 1 : u.platforms.includes(p);

// `skip` deja fuera un filtro: así cada grupo cuenta sus opciones con los demás
// filtros puestos (conteos facetados), que es donde viven los números.
export function filterUnits(units: CuadreUnit[], f: UnitFilters, skip?: keyof UnitFilters): CuadreUnit[] {
  const q = norm(f.q.trim());
  return units.filter(
    (u) =>
      (skip === "q" || !q || [u.imei, u.name, u.plates, u.accountName].some((v) => norm(v).includes(q))) &&
      (skip === "billing" || !f.billing || u.billing === f.billing) &&
      (skip === "report" || !f.report || (f.report === "silent" ? u.report !== "ok" : u.report === f.report)) &&
      (skip === "platform" || !f.platform || matchPlatform(u, f.platform)) &&
      (skip === "condition" || !f.condition || (f.condition === "-" ? !u.condition : u.condition === f.condition)) &&
      (skip === "account" || f.account == null || u.accountId === f.account) &&
      (skip === "mismatch" || !f.mismatch || u.accountMismatch) &&
      (skip === "check" || !f.check || u[f.check])
  );
}

export function countBy<T extends string>(units: CuadreUnit[], key: (u: CuadreUnit) => T | T[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const u of units) {
    const k = key(u);
    for (const v of Array.isArray(k) ? k : [k]) out[v] = (out[v] ?? 0) + 1;
  }
  return out;
}

export const platformKeys = (u: CuadreUnit): PlatformFilter[] =>
  u.platforms.length === 0 ? ["none"] : u.platforms.length > 1 ? [...u.platforms, "multi"] : u.platforms;

// Vistas rápidas: combinaciones de filtros que responden las preguntas del
// cuadre. Son los únicos números grandes de la pantalla.
export const QUICK_VIEWS = {
  billableSilent: { billing: "billable", report: "silent" },
  notBillableReporting: { billing: "notBillable", report: "ok" },
  noPlatform: { billing: "billable", platform: "none" },
  multi: { platform: "multi" },
  mismatch: { mismatch: true },
} satisfies Record<string, Partial<UnitFilters>>;

export type QuickView = keyof typeof QUICK_VIEWS;

export const applyQuick = (v: QuickView): UnitFilters => ({ ...EMPTY_FILTERS, ...QUICK_VIEWS[v] });

export const isQuick = (f: UnitFilters, v: QuickView) => {
  const target = applyQuick(v);
  return (Object.keys(EMPTY_FILTERS) as (keyof UnitFilters)[]).every((k) => k === "q" || f[k] === target[k]);
};

// ── Cuentas ─────────────────────────────────────────────────────────────────

export interface CuadreAccount {
  id: number;
  name: string;
  billable: boolean;
  condition: string | null;
  sgOrigin: SgPlatform[];
  realOrigin: SgPlatform[];
  mismatch: boolean;
  units: number; // equipos activos en SG
  byPlatform: Record<SgPlatform | "none", number>;
  reporting: number;
  silent: number; // activos sin reporte en 24 h (incluye sin dato)
}

export function buildAccounts(units: CuadreUnit[], accounts: RawAccount[]): CuadreAccount[] {
  const rows = new Map<number, CuadreAccount>();
  for (const a of accounts) {
    rows.set(a.id, {
      id: a.id,
      name: a.name,
      billable: a.billable,
      condition: a.condition,
      sgOrigin: PLATFORMS.filter((p) => a.origin.includes(p)),
      realOrigin: [],
      mismatch: false,
      units: 0,
      byPlatform: { v5: 0, wialon: 0, mapon: 0, none: 0 },
      reporting: 0,
      silent: 0,
    });
  }
  const real = new Map<number, Set<SgPlatform>>();
  for (const u of units) {
    const r = u.accountId != null ? rows.get(u.accountId) : undefined;
    if (!r) continue;
    if (u.accountMismatch) r.mismatch = true;
    const s = real.get(r.id) ?? new Set<SgPlatform>();
    u.platforms.forEach((p) => s.add(p));
    real.set(r.id, s);
    if (u.billing === "inactive") continue;
    r.units++;
    if (u.platforms.length) u.platforms.forEach((p) => r.byPlatform[p]++);
    else r.byPlatform.none++;
    if (u.report === "ok") r.reporting++;
    else r.silent++;
  }
  for (const [id, s] of real) {
    const r = rows.get(id);
    if (r) r.realOrigin = PLATFORMS.filter((p) => s.has(p));
  }
  return [...rows.values()];
}
